import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { get } from '@/api/client';
import { Avatar } from '@/components/atoms/Avatar';
import { LevelBadge } from '@/components/atoms/LevelBadge';
import { Skeleton } from '@/components/atoms/Skeleton';

type Tab = 'following' | 'followers';

interface User {
  user_id: string;
  nickname: string;
  profile_image_url: string | null;
  level: number;
  bio: string | null;
}

export function FollowingPage() {
  const [tab, setTab] = useState<Tab>('following');

  const { data, isLoading } = useQuery({
    queryKey: ['follow', tab],
    queryFn: () => get<{ list: User[] }>(`/follow/${tab}`),
  });

  return (
    <div className="container-base py-10">
      <h1 className="mb-4 text-2xl font-extrabold">팔로우</h1>

      {/* 탭 */}
      <div className="mb-4 flex gap-2 border-b border-[var(--color-border)]">
        {(['following', 'followers'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium ${
              tab === t
                ? 'border-b-2 border-[var(--color-primary)] text-[var(--color-primary)]'
                : 'text-[var(--color-text-sub)]'
            }`}
          >
            {t === 'following' ? '팔로잉' : '팔로워'}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      )}

      <div className="space-y-2">
        {data?.list?.map((u) => (
          <Link
            key={u.user_id}
            to={`/users/${u.user_id}`}
            className="flex items-center gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3 hover:border-[var(--color-primary)]/30"
          >
            <Avatar src={u.profile_image_url ?? undefined} alt={u.nickname} size="md" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold">{u.nickname}</span>
                <LevelBadge level={u.level} size="sm" />
              </div>
              {u.bio && (
                <p className="text-xs text-[var(--color-text-sub)] line-clamp-1">{u.bio}</p>
              )}
            </div>
          </Link>
        ))}
        {data?.list?.length === 0 && (
          <div className="rounded-xl border border-dashed border-[var(--color-border)] py-12 text-center text-[var(--color-text-sub)]">
            {tab === 'following' ? '아직 팔로잉 중인 사람이 없어요.' : '아직 팔로워가 없어요.'}
          </div>
        )}
      </div>
    </div>
  );
}
